import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { AdminProfile, AdminSession, RoleCode } from '@/types/admin'
import { ADMIN_ROLES, ROLE_PERMISSIONS } from '@/data/roles'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { useSessionCacheStore } from './session-cache'
import { useWorkspaceStore } from './workspace'

const SESSION_KEY = 'kanghu.admin.session.v1'

function readSession(): AdminSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AdminSession
    if (!parsed?.profile || !parsed?.token) return null
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) return null
    return parsed
  } catch {
    return null
  }
}

function persist(session: AdminSession | null): void {
  if (typeof window === 'undefined') return
  if (!session) window.sessionStorage.removeItem(SESSION_KEY)
  else window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

function roleLabel(role: string): string {
  return ADMIN_ROLES.find(item => item.code === role)?.name ?? '管理员'
}

/**
 * Tabs, filters, column settings and scroll positions are account-scoped.
 * They live in sessionStorage only for same-account reload continuity, so a
 * different login must never inherit another administrator's workspace.
 */
function resetAccountState(): void {
  try {
    useWorkspaceStore().clearAllStored()
    useSessionCacheStore().clear()
  } catch {
    // Store setup is unavailable in isolated unit tests; browser flows always
    // have an active Pinia instance by the time login/logout executes.
  }
}

export const useAuthStore = defineStore('auth', () => {
  const session = ref<AdminSession | null>(null)
  const viewAsRole = ref<RoleCode | null>(null)
  const loading = ref(false)
  const loginError = ref('')
  const loginTrace = ref('')
  const bootstrapped = ref(false)
  const failedAttempts = ref(0)
  const lockedUntil = ref(0)
  const clock = ref(Date.now())
  let lockTimer: number | null = null

  const locked = computed(() => lockedUntil.value > clock.value)
  const lockRemaining = computed(() => Math.max(0, Math.ceil((lockedUntil.value - clock.value) / 1000)))
  const attemptsRemaining = computed(() => Math.max(0, 5 - failedAttempts.value))

  const primaryRole = computed<RoleCode>(() => session.value?.roles?.[0] ?? 'operations_manager')
  const effectiveRole = computed<RoleCode>(() => viewAsRole.value ?? primaryRole.value)
  const isAuthenticated = computed(() => Boolean(session.value?.token))

  const permissions = computed<string[]>(() => {
    if (!session.value) return []
    const roles = viewAsRole.value ? [viewAsRole.value] : session.value.roles
    const values = new Set<string>()
    // Role permission edits are stored in the same reactive memory database as
    // the roles table. Reading through the adapter keeps the authenticated
    // menu, route guard, directive and page actions in sync with that edit
    // instead of silently falling back to the frozen seed matrix.
    for (const role of roles) {
      for (const permission of adapter.permissionsForRole(role)) values.add(permission)
    }
    return Array.from(values)
  })

  const profile = computed<AdminProfile | null>(() => session.value?.profile ?? null)
  const roleName = computed(() => roleLabel(effectiveRole.value))
  const isViewingAs = computed(() => Boolean(viewAsRole.value))
  const canPreviewRoles = computed(() => primaryRole.value === 'super_admin')

  function restore(): void {
    const stored = readSession()
    if (stored) {
      session.value = stored
      viewAsRole.value = stored.viewAsRole ?? null
    }
    bootstrapped.value = true
  }

  function startLockClock(): void {
    if (typeof window === 'undefined' || lockTimer !== null) return
    lockTimer = window.setInterval(() => {
      clock.value = Date.now()
      if (!locked.value && lockTimer !== null) {
        window.clearInterval(lockTimer)
        lockTimer = null
      }
    }, 1000)
  }

  function registerFailure(): void {
    failedAttempts.value += 1
    if (failedAttempts.value >= 5) {
      lockedUntil.value = Date.now() + 15 * 60_000
      clock.value = Date.now()
      startLockClock()
    }
  }

  function has(permission: string | undefined): boolean {
    if (!permission) return true
    if (permission === 'public') return true
    if (permission === 'authenticated') return isAuthenticated.value
    if (permission === '*') return isAuthenticated.value
    if (!isAuthenticated.value) return false
    return permissions.value.includes(permission)
  }

  function hasAll(required: string[]): boolean {
    return required.every(permission => has(permission))
  }

  async function login(account: string, password: string, remember = true): Promise<AdminSession> {
    loading.value = true
    loginError.value = ''
    loginTrace.value = ''
    try {
      if (locked.value) {
        throw new ApiError('422', `登录失败次数过多，请在 ${Math.ceil(lockRemaining.value / 60)} 分钟后重试`)
      }
      const result = await request(
        () => {
          const admin = adapter.findAdminByAccount(account.trim())
          if (!admin) throw new ApiError('422', '账号或密码不正确，请重新输入', { account: '账号不存在' })
          if (admin.status !== 'active') throw new ApiError('403', '该管理员账号已停用，请联系平台安全管理员')
          if (password.length < 6) throw new ApiError('422', '访问口令至少 6 位', { password: '口令过短' })
          if (locked.value) throw new ApiError('422', `登录失败次数过多，请在 ${Math.ceil(lockRemaining.value / 60)} 分钟后重试`)
          const roles = (admin.role_codes as RoleCode[]) ?? []
          const role = roles[0] ?? 'operations_manager'
          const next: AdminSession = {
            token: `proto-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
            profile: {
              adminNo: String(admin.admin_no ?? ''),
              name: String(admin.name ?? ''),
              account: String(admin.account ?? ''),
              phone: String(admin.phone ?? ''),
              email: String(admin.email ?? ''),
              role,
              roleLabel: roleLabel(role),
              organization: '康护健康 · 平台运营中心',
            },
            permissions: (roles.flatMap(item => ROLE_PERMISSIONS[item] ?? [])),
            roles,
            issuedAt: Date.now(),
            expiresAt: Date.now() + 8 * 3_600_000,
            viewAsRole: null,
          }
          adapter.table('audit_logs').unshift({
            id: `audit-login-${Date.now()}`,
            actor_name: String(admin.name),
            actor_account: String(admin.account),
            module: '登录',
            action: '登录',
            resource_label: '管理端会话',
            result: '成功',
            ip: '10.24.18.32',
            request_no: `REQ-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 9000 + 1000)}`,
            detail: '管理员通过原型登录页建立内存会话',
            created_at: new Date().toISOString(),
            row_version: 1,
          })
          return next
        },
        { write: true, delay: 420 },
      )
      session.value = result
      viewAsRole.value = null
      failedAttempts.value = 0
      lockedUntil.value = 0
      if (remember) persist(result)
      else persist(null)
      resetAccountState()
      return result
    } catch (error) {
      if (error instanceof ApiError && error.code === '422' && !locked.value) registerFailure()
      loginError.value = locked.value
        ? `登录失败次数过多，账号已临时锁定，请在 ${Math.ceil(lockRemaining.value / 60)} 分钟后重试`
        : error instanceof Error ? error.message : '登录未完成，请稍后重试'
      loginTrace.value = error instanceof ApiError ? error.traceId ?? '' : ''
      throw error
    } finally {
      loading.value = false
    }
  }

  function logout(reason = ''): void {
    if (session.value) {
      adapter.table('audit_logs').unshift({
        id: `audit-logout-${Date.now()}`,
        actor_name: session.value.profile.name,
        actor_account: session.value.profile.account,
        module: '登录',
        action: '退出',
        resource_label: '管理端会话',
        result: '成功',
        ip: '10.24.18.32',
        request_no: `REQ-${Date.now()}`,
        detail: reason || '管理员主动退出',
        created_at: new Date().toISOString(),
        row_version: 1,
      })
    }
    session.value = null
    viewAsRole.value = null
    persist(null)
    resetAccountState()
  }

  function switchViewAs(role: RoleCode | null): void {
    if (!canPreviewRoles.value) return
    viewAsRole.value = role
    if (session.value) {
      session.value.viewAsRole = role
      persist(session.value)
    }
  }

  function refreshProfile(patch: Partial<AdminProfile>): void {
    if (!session.value) return
    session.value.profile = { ...session.value.profile, ...patch }
    persist(session.value)
  }

  return {
    session,
    loading,
    loginError,
    loginTrace,
    bootstrapped,
    failedAttempts,
    locked,
    lockRemaining,
    attemptsRemaining,
    viewAsRole,
    primaryRole,
    effectiveRole,
    isAuthenticated,
    permissions,
    profile,
    roleName,
    isViewingAs,
    canPreviewRoles,
    restore,
    bootstrap: restore,
    has,
    hasAll,
    login,
    logout,
    switchViewAs,
    refreshProfile,
  }
})
